import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Calendar, Search, Download, FileText, X,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 100;

const BundleDetailReport = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // The text input the user is typing in. `appliedSearch` is what we
  // actually send to the backend — keeps the input snappy while
  // debouncing remote queries.
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  // Default range = last 30 days.
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  // Debounce the universal search input (350ms).
  useEffect(() => {
    const handle = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    try {
      const res = await apiClient.get(
        '/sections/greige/reports/bundle-detail',
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            search: appliedSearch || undefined,
            page,
            page_size: PAGE_SIZE,
          },
        },
      );
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load report';
      toast.error(msg);
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, appliedSearch, page]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExportCSV = async () => {
    if (!startDate || !endDate) {
      toast.warning('Pick a start and end date first.');
      return;
    }
    setExporting(true);
    try {
      const res = await apiClient.get(
        '/sections/greige/reports/bundle-detail/export-csv',
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            search: appliedSearch || undefined,
          },
          responseType: 'blob',
        },
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `greige-bundle-detail-${startDate}-to-${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded.');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Export failed';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setAppliedSearch('');
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatDate = (iso) => {
    if (!iso) return '—';
    return iso.slice(0, 10);
  };

  const headerActions = useMemo(() => (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={fetchReport}
        disabled={isLoading}
        data-testid="bundle-detail-refresh-btn"
      >
        {isLoading
          ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          : <RefreshCw className="w-4 h-4 mr-2" />}
        Refresh
      </Button>
      <Button
        size="sm"
        onClick={handleExportCSV}
        disabled={exporting || total === 0}
        className="bg-emerald-600 hover:bg-emerald-700"
        data-testid="bundle-detail-export-csv-btn"
      >
        <Download className="w-4 h-4 mr-2" />
        {exporting ? 'Exporting…' : 'Export CSV'}
      </Button>
    </div>
  ), [fetchReport, isLoading, handleExportCSV, exporting, total]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 pt-16" data-testid="greige-bundle-detail-report">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/sections/greige')}
              data-testid="back-to-greige-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Greige
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-emerald-700" />
                Bundle Detail Report
              </h1>
              <p className="text-sm text-slate-600">
                Supplier, sort no, color and bundle number — filtered by date and universal search.
              </p>
            </div>
          </div>
          {headerActions}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="w-4 h-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label htmlFor="start-date" className="text-xs text-slate-600">Start Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                    className="pl-8"
                    data-testid="filter-start-date"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="end-date" className="text-xs text-slate-600">End Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                    className="pl-8"
                    data-testid="filter-end-date"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="universal-search" className="text-xs text-slate-600">
                  Universal Filter (matches any column)
                </Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    id="universal-search"
                    type="text"
                    placeholder="Search supplier, sort no, color, or bundle number…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-8 pr-8"
                    data-testid="filter-universal-search"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => setSearchInput('')}
                      className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
                      data-testid="clear-search-btn"
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-slate-600">
                {isLoading
                  ? 'Loading…'
                  : <>
                      Showing <span className="font-semibold text-slate-900" data-testid="result-count">{rows.length}</span> of{' '}
                      <span className="font-semibold text-slate-900" data-testid="total-count">{total}</span> bundle(s)
                      {appliedSearch && (
                        <Badge variant="secondary" className="ml-2">
                          search: “{appliedSearch}”
                        </Badge>
                      )}
                    </>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" /> Clear filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table data-testid="bundle-detail-table">
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="w-12 text-center">#</TableHead>
                  <TableHead>Supplier Name</TableHead>
                  <TableHead>Sort No</TableHead>
                  <TableHead>Color Name</TableHead>
                  <TableHead>Bundle Number</TableHead>
                  <TableHead>Inspection</TableHead>
                  <TableHead className="text-right">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />
                      Loading bundle details…
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500" data-testid="empty-state">
                      No bundles match the current filters.
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && rows.map((r, idx) => (
                  <TableRow key={`${r.bundle_number}-${idx}`} data-testid={`bundle-row-${idx}`}>
                    <TableCell className="text-center text-slate-500 text-xs">
                      {(page - 1) * PAGE_SIZE + idx + 1}
                    </TableCell>
                    <TableCell className="font-medium" data-testid={`row-supplier-${idx}`}>
                      {r.supplier_name || <span className="text-slate-400 italic">—</span>}
                    </TableCell>
                    <TableCell data-testid={`row-sort-no-${idx}`}>
                      {r.sort_no || <span className="text-slate-400 italic">—</span>}
                    </TableCell>
                    <TableCell data-testid={`row-color-${idx}`}>
                      <div className="flex items-center gap-2">
                        {r.color_code && (
                          <span
                            className="inline-block w-3 h-3 rounded-full border border-slate-300"
                            style={{ backgroundColor: r.color_code.startsWith('#') ? r.color_code : `#${r.color_code}` }}
                          />
                        )}
                        <span>{r.color_name || <span className="text-slate-400 italic">—</span>}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm" data-testid={`row-bundle-${idx}`}>
                      {r.bundle_number}
                    </TableCell>
                    <TableCell data-testid={`row-inspection-${idx}`}>
                      {(() => {
                        const v = r.inspection_result || '-';
                        if (v === '-') return <span className="text-slate-400 italic">—</span>;
                        const cls = v === 'Not Inspected'
                          ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : v === 'Damage'
                            ? 'bg-red-100 text-red-800 border-red-300'
                            : 'bg-emerald-100 text-emerald-800 border-emerald-300';
                        return <Badge variant="outline" className={cls}>{v}</Badge>;
                      })()}
                    </TableCell>
                    <TableCell className="text-right text-slate-600 text-sm" data-testid={`row-date-${idx}`}>
                      {formatDate(r.effective_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2" data-testid="pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="page-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </Button>
            <span className="text-sm text-slate-600">
              Page <span className="font-semibold" data-testid="current-page">{page}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || isLoading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="page-next-btn"
            >
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BundleDetailReport;
